import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Calendar,
  RefreshCw,
  Download,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const Maintenance = () => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [currentMonth, setCurrentMonth] = useState(new Date(2026, 0)); // January 2026

  const maintenanceTasks = [
    {
      id: 'PM-001',
      name: 'Pump A-123 Oil Change',
      asset: 'Pump A-123',
      type: 'Preventive',
      frequency: 'Monthly',
      lastCompleted: '2025-12-15',
      nextDue: '2026-01-25',
      assignee: 'John Smith',
      duration: '2 hours',
      status: 'scheduled',
      priority: 'high'
    },
    {
      id: 'PM-002',
      name: 'Motor B-456 Inspection',
      asset: 'Motor B-456',
      type: 'Inspection',
      frequency: 'Quarterly',
      lastCompleted: '2025-10-20',
      nextDue: '2026-01-26',
      assignee: 'Sarah Jones',
      duration: '1 hour',
      status: 'scheduled',
      priority: 'medium'
    },
    {
      id: 'PM-003',
      name: 'Compressor C-789 Filter Change',
      asset: 'Compressor C-789',
      type: 'Preventive',
      frequency: 'Bi-monthly',
      lastCompleted: '2025-11-15',
      nextDue: '2026-01-28',
      assignee: 'Mike Brown',
      duration: '3 hours',
      status: 'scheduled',
      priority: 'high'
    },
    {
      id: 'PM-004',
      name: 'Conveyor D-012 Belt Inspection',
      asset: 'Conveyor D-012',
      type: 'Inspection',
      frequency: 'Weekly',
      lastCompleted: '2026-01-18',
      nextDue: '2026-01-30',
      assignee: 'Emily Davis',
      duration: '30 minutes',
      status: 'overdue',
      priority: 'high'
    },
    {
      id: 'PM-005',
      name: 'Press E-345 Calibration',
      asset: 'Press E-345',
      type: 'Calibration',
      frequency: 'Annual',
      lastCompleted: '2025-01-10',
      nextDue: '2026-02-02',
      assignee: 'David Wilson',
      duration: '4 hours',
      status: 'scheduled',
      priority: 'medium'
    },
  ];

  const scheduleTemplates = [
    { name: 'Pump Maintenance', frequency: 'Monthly', tasks: 8, assets: 12 },
    { name: 'Motor Inspection', frequency: 'Quarterly', tasks: 5, assets: 8 },
    { name: 'Safety Check', frequency: 'Weekly', tasks: 15, assets: 25 },
    { name: 'Annual Calibration', frequency: 'Annual', tasks: 3, assets: 5 },
  ];

  const calendarDays = Array.from({ length: 31 }, (_, i) => i + 1);
  
  const getMaintenanceForDay = (day) => {
    return maintenanceTasks.filter(task => {
      const taskDate = new Date(task.nextDue);
      return taskDate.getDate() === day && taskDate.getMonth() === currentMonth.getMonth();
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'in-progress': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Preventive Maintenance</h1>
          <p className="text-gray-600 mt-1">Schedule and manage recurring maintenance tasks</p>
        </div>
        <div className="flex gap-3">
          <button className="btn btn-outline">
            <Download className="w-5 h-5 mr-2" />
            {t('common.export')} Schedule
          </button>
          <Link to="/portal/cmms/maintenance/new" className="btn btn-primary">
            <Plus className="w-5 h-5 mr-2" />
            {t('common.create')} Schedule
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-gray-900">45</div>
          <div className="text-sm text-gray-600">Total Schedules</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-blue-600">23</div>
          <div className="text-sm text-gray-600">This Month</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-green-600">18</div>
          <div className="text-sm text-gray-600">{t('cmms.completed')}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-red-600">3</div>
          <div className="text-sm text-gray-600">Overdue</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-2xl font-bold text-yellow-600">87%</div>
          <div className="text-sm text-gray-600">Completion Rate</div>
        </div>
      </div>

      {/* View Toggle & Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1 rounded text-sm ${viewMode === 'calendar' ? 'bg-white shadow-sm' : ''}`}
            >
              <Calendar className="w-4 h-4 inline mr-1" />
              Calendar
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 rounded text-sm ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
            >
              List
            </button>
          </div>

          <select className="px-4 py-2 border border-gray-300 rounded-lg">
            <option>{t('common.allTypes')}</option>
            <option>Preventive</option>
            <option>Inspection</option>
            <option>Calibration</option>
          </select>

          <select className="px-4 py-2 border border-gray-300 rounded-lg">
            <option>All Frequencies</option>
            <option>Daily</option>
            <option>Weekly</option>
            <option>Monthly</option>
            <option>Quarterly</option>
            <option>Annual</option>
          </select>

          <select className="px-4 py-2 border border-gray-300 rounded-lg">
            <option>All Assignees</option>
            <option>John Smith</option>
            <option>Sarah Jones</option>
            <option>Mike Brown</option>
          </select>
        </div>
      </div>

      {viewMode === 'calendar' ? (
        <div className="space-y-6">
          {/* Calendar View */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={previousMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {/* Day Headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center font-semibold text-gray-600 text-sm py-2">
                  {day}
                </div>
              ))}

              {/* Calendar Days */}
              {calendarDays.map(day => {
                const tasks = getMaintenanceForDay(day);
                
                return (
                  <div
                    key={day}
                    className={`min-h-[100px] border border-gray-200 rounded-lg p-2 hover:shadow-md transition-shadow ${
                      day === 22 ? 'bg-blue-50 border-blue-300' : 'bg-white'
                    }`}
                  >
                    <div className="text-sm font-semibold text-gray-700 mb-2">{day}</div>
                    <div className="space-y-1">
                      {tasks.map(task => (
                        <div
                          key={task.id}
                          className={`text-xs p-1 rounded ${
                            task.priority === 'high' ? 'bg-red-100 text-red-800' :
                            task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}
                        >
                          <div className="font-medium truncate">{task.asset}</div>
                          <div className="text-xs opacity-75">{task.assignee}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asset</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.type')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.wo_dueDate')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assignee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.priority')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {maintenanceTasks.map((task) => (
                <tr key={task.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {task.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{task.name}</div>
                    <div className="text-xs text-gray-500">{task.duration}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {task.asset}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {task.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <RefreshCw className="w-3 h-3" />
                      {task.frequency}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {task.nextDue}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {task.assignee}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                      {task.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                      {task.priority}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Schedule Templates */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Schedule Templates</h2>
          <button className="text-primary-600 hover:text-primary-700 text-sm font-medium">
            {t('common.viewAll')}
          </button>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {scheduleTemplates.map((template, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 truncate">{template.name}</div>
                  <div className="text-xs text-gray-500">{template.frequency}</div>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>{template.tasks} tasks</span>
                <span>{template.assets} assets</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Maintenance;
